import { useHomeContext } from "../../contexts/HomeContext";
import CardLoader from "../CardLoader";
import Text from "../Text";
import CircularProgressBar from "../CircularProgressBar";
import Container from "../Container";
import Currency from "../Currency";
import moment from "moment";
import { Button } from "primereact/button";
import { OverlayPanel } from "primereact/overlaypanel";
import { useEffect, useMemo, useRef } from "react";
import Label from "../Label";
import { InputNumber } from "primereact/inputnumber";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Calendar } from "primereact/calendar";
import useAuthHttp from "../../hooks/useAuthHttp";
import { useAuthContext } from "../../contexts/AuthContext";
import { Toast } from "primereact/toast";
import Icon from "../Icon";
import { AnalyticsEvent, Tracker } from "../../analytics";

export default function GoalViewCard() {
    const homeContext = useHomeContext();
    const authContext = useAuthContext();
    const authHttp = useAuthHttp();
    const goalHandleRef = useRef<OverlayPanel | null>(null);
    const toastRef = useRef<Toast | null>(null);

    const goalFormik = useFormik({
        initialValues: {
            asset_goal_value: homeContext.goal?.asset_goal_value,
            asset_goal_due_date: homeContext.goal?.asset_goal_due_date
        },
        validationSchema: Yup.object().shape({
            asset_goal_value: Yup.number().required("Campo obrigatório"),
            asset_goal_due_date: Yup.string().required("Campo obrigatório")
        }),
        onSubmit: async (values) => {
            const payload = {
                user_id: authContext.user.user_id,
                asset_goal_due_date: values.asset_goal_due_date,
                asset_goal_value: values.asset_goal_value
            };

            try {
                if (homeContext.goal?.id) {
                    const response = await authHttp.doPatch(`/user/goal/${homeContext.goal.id}`, payload);

                    await homeContext.refresh();

                    Tracker.track(AnalyticsEvent.HomeGoalSaved);

                    toastRef.current?.show({
                        severity: "success",
                        summary: "Sucesso",
                        detail: "Meta salva com sucesso"
                    });

                    goalHandleRef.current?.hide();

                    return response;
                }

                const response = await authHttp.doPost("/user/goal", payload);

                await homeContext.refresh();

                Tracker.track(AnalyticsEvent.HomeGoalSaved);

                toastRef.current?.show({
                    severity: "success",
                    summary: "Sucesso",
                    detail: "Meta salva com sucesso"
                });

                goalHandleRef.current?.hide();

                return response;
            } catch (err) {
                Tracker.trackError('GoalViewCard', 'unknown');
                toastRef.current?.show({
                    severity: "error",
                    summary: "Erro",
                    detail: "Ocorreu um erro ao salvar a meta"
                });
            }
        }
    });

    useEffect(() => {
        goalFormik.setValues({
            asset_goal_due_date: homeContext.goal?.asset_goal_due_date,
            asset_goal_value: homeContext.goal?.asset_goal_value
        });
    }, [homeContext.goal?.asset_goal_value, homeContext.goal?.asset_goal_due_date]);

    const goalPercentage = useMemo(() => {
        return (homeContext.goal?.asset_goal_value ?? 0) === 0 ? 0 : Math.round(((homeContext.wallet?.totalValue ?? 0) / (homeContext.goal?.asset_goal_value ?? 0)) * 100);
    }, [homeContext.goal?.asset_goal_value, homeContext.wallet?.totalValue]);

    return (
        <>
            <Toast ref={toastRef} />

            <Container wrap cardResponsive flexColumn center className="bg-white border-round p-3" gap={4} relative>
                {
                    homeContext.isLoading
                        ? (
                            <CardLoader />
                        )
                        : <Container flexColumn fullWidth gap={3} className="flex-1" paddingVertical="3">
                            <Container className="justify-content-between align-items-center">
                                <Text color="800" fontWeight="semibold">Meta</Text>

                                <Icon
                                    className="cursor-pointer"
                                    onClick={(e) => {
                                        if (homeContext.goal?.id) {
                                            Tracker.track(AnalyticsEvent.HomeGoalEditOpened);
                                        } else {
                                            Tracker.track(AnalyticsEvent.HomeGoalRegisterTapped);
                                        }
                                        goalHandleRef.current?.toggle(e);
                                    }}
                                    name="pi-cog"
                                />

                                <OverlayPanel ref={goalHandleRef} showCloseIcon>
                                    <Container flexColumn gap={3} maxWidth={300}>
                                        <Container flexColumn gap={1}>
                                            <Label>Valor da Meta</Label>
                                            <InputNumber
                                                value={goalFormik.values.asset_goal_value}
                                                onValueChange={(e) => goalFormik.setFieldValue("asset_goal_value", e.value)}
                                                mode="currency"
                                                currency="BRL"
                                                locale="pt-BR"
                                            />
                                        </Container>

                                        <Container flexColumn gap={1}>
                                            <Label>Data da Meta</Label>
                                            <Calendar
                                                value={goalFormik.values.asset_goal_due_date ? new Date(goalFormik.values.asset_goal_due_date) : new Date()}
                                                onChange={(e) => goalFormik.setFieldValue("asset_goal_due_date", e.value?.toISOString())}
                                                locale="pt"
                                            />
                                        </Container>

                                        <Button
                                            onClick={() => goalFormik.handleSubmit()}
                                            className="flex justify-content-center gap-2"
                                            loading={goalFormik.isSubmitting}
                                        >
                                            {goalFormik.isSubmitting ? "Salvando..." : "Salvar"}
                                        </Button>
                                    </Container>
                                </OverlayPanel>
                            </Container>
                            {
                                !homeContext.wallet?.totalValue
                                    ? <Text>Sua carteira está vazia</Text>
                                    : (
                                        <Container flexColumn center between className="flex-1 justify-content-center" fullWidth gap={3}>
                                            <Container between fullWidth>
                                                <Container flexColumn gap={1}>
                                                    <Text color="400" fontWeight="light" size="xs">Atingir</Text>

                                                    <Currency value={homeContext.goal?.asset_goal_value ?? 0}>
                                                        {(value) => (<Text color="800" fontWeight="semibold">{value}</Text>)}
                                                    </Currency>
                                                </Container>

                                                <Container flexColumn gap={1}>
                                                    <Text color="400" fontWeight="light" size="xs">Até</Text>

                                                    <Text color="800" fontWeight="semibold">{homeContext.goal?.asset_goal_due_date ? moment(homeContext.goal?.asset_goal_due_date).format("DD/MM/YYYY") : "Não informado"}</Text>
                                                </Container>
                                            </Container>

                                            <CircularProgressBar percentage={goalPercentage} />
                                        </Container>
                                    )
                            }
                        </Container>
                }
            </Container>
        </>
    )
}